/** One entry of the backend's `details` array (docs/api.md 37.1). */
export interface FieldError {
  field: string;
  code: string;
  message: string;
}

/** The backend's error envelope. */
export interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    details?: FieldError[];
    attempts_remaining?: number;
  };
}

/** Client-side codes, never sent by the backend. */
export const CLIENT_CODES = {
  network: "NETWORK",
  timeout: "TIMEOUT",
  aborted: "ABORTED",
  badResponse: "BAD_RESPONSE",
  /** A gateway (nginx, Cloudflare, the dev proxy) answered 502/503/504 for an API that is down. */
  unreachable: "UNREACHABLE",
} as const;

const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * Every failed call becomes one of these. Screens route on `code` (the
 * backend's, or one of CLIENT_CODES), show `message`, and map `details[].field`
 * onto their inputs.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: FieldError[];
  readonly retryAfterSec: number | null;
  readonly attemptsRemaining: number | null;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    details?: FieldError[];
    retryAfterSec?: number | null;
    attemptsRemaining?: number | null;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.details = init.details ?? [];
    this.retryAfterSec = init.retryAfterSec ?? null;
    this.attemptsRemaining = init.attemptsRemaining ?? null;
  }

  /** No answer from the server at all: the one case an offline queue retries. */
  get isNetwork(): boolean {
    return (this.status === 0 && this.code !== CLIENT_CODES.aborted) || this.code === CLIENT_CODES.unreachable;
  }

  /** docs/frontend-integration.md 8: retry only network failures and 5xx. */
  get isRetryable(): boolean {
    return this.isNetwork || this.status >= 500;
  }

  /** The first detail for a field path such as `profile.company_name`. */
  detailFor(field: string): FieldError | undefined {
    return this.details.find((d) => d.field === field);
  }
}

/**
 * The code for a failed response: the backend's own when it sent the envelope;
 * otherwise a gateway status means the API itself is not answering.
 */
export function codeForResponse(status: number, envelopeCode: string | undefined): string {
  if (envelopeCode) return envelopeCode;
  if (GATEWAY_STATUSES.has(status)) return CLIENT_CODES.unreachable;
  return `HTTP_${status}`;
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

export function hasCode(e: unknown, ...codes: string[]): e is ApiError {
  return isApiError(e) && codes.includes(e.code);
}
